import licenses from '@/enums/basic/license.json';
import { cn } from '@/utils/shadcn';
import React from 'react';
import { FormattedMessage } from 'react-intl';

export const licenseUrl2enum: Record<string, string> = {
  '//creativecommons.org/publicdomain/zero/1.0/legalcode': 'CC0_1_0',
  '//creativecommons.org/licenses/by/4.0/legalcode': 'CC_BY_4_0',
  '//creativecommons.org/licenses/by-nc/4.0/legalcode': 'CC_BY_NC_4_0',
  unsupported: 'UNSUPPORTED',
  unspecified: 'UNSPECIFIED',
};

export const enum2licenseUrl: Record<string, string> = {
  CC0_1_0: '//creativecommons.org/publicdomain/zero/1.0/legalcode',
  CC_BY_4_0: '//creativecommons.org/licenses/by/4.0/legalcode',
  CC_BY_NC_4_0: '//creativecommons.org/licenses/by-nc/4.0/legalcode',
};

export function LicenceTag({
  value,
  ...props
}: {
  value?: string | null;
  className?: string;
} & React.ComponentProps<typeof IdentifierTag>) {
  const fallback = value ?? 'unknown';
  const val = fallback.replace(/http(s)?:/, '');
  let licenceEnum = licenseUrl2enum[val] || fallback;
  if (licenses.indexOf(licenceEnum) === -1) licenceEnum = 'UNSUPPORTED';
  const url = enum2licenseUrl[licenceEnum];

  const licenseProps = url ? { as: 'a' as const, href: url } : {};

  return (
    <IdentifierTag {...licenseProps} {...props}>
      <IdentifierType>
        <FormattedMessage id="phrases.license" />
      </IdentifierType>
      <IdentifierValue>
        <FormattedMessage id={`enums.license.${licenceEnum}`} />
      </IdentifierValue>
    </IdentifierTag>
  );
}

const testSite = import.meta.env.PUBLIC_TEST_SITE === 'true';
const testSiteDoiPrefix = testSite ? 'Do not cite DOI' : 'DOI';
export function DoiTag({ id = '', className = '', ...props }) {
  const sanitizedId = id.replace(/^(.*doi.org\/)?(doi:)?(10\.)/, '10.');
  return (
    <IdentifierTag
      as="a"
      href={`https://doi.org/${sanitizedId}`}
      className={cn(className, testSite ? 'g-select-none g-pointer-events-none' : '')}
      {...props}
    >
      <IdentifierType>{testSiteDoiPrefix}</IdentifierType>
      <IdentifierValue className={testSite ? 'g-text-red-500' : ''}>{sanitizedId}</IdentifierValue>
    </IdentifierTag>
  );
}

export function OrcId({ href, className }: { href: string; className?: string }) {
  return (
    <a dir="ltr" className={cn('g-inline-block g-no-underline', className)} href={href}>
      <img
        alt="ORCID logo"
        className="g-me-1 g-inline-block"
        src={import.meta.env.PUBLIC_BASE_URL + '/img/public/orcid_16x16.gif'}
        width="16"
        height="16"
      />
      {href}
    </a>
  );
}

export function Lsid({ identifier = '', ...props }) {
  return (
    <IdentifierTag as="a" href={`http://lsid.info/${identifier}`} {...props}>
      <IdentifierType>URN:LSID:</IdentifierType>
      <IdentifierValue>{identifier.replace('urn:lsid:', '')}</IdentifierValue>
    </IdentifierTag>
  );
}

export function IucnTag({
  statusCategory,
  ...props
}: {
  statusCategory: string;
} & React.ComponentProps<typeof IdentifierTag>) {
  return (
    <IdentifierTag {...props}>
      <IdentifierType className="g-bg-[#ed2a24] g-border-[#c1201b]">IUCN</IdentifierType>
      <IdentifierValue>
        <FormattedMessage id={`enums.threatStatus.${statusCategory}`} />
      </IdentifierValue>
    </IdentifierTag>
  );
}

export function IdentifierType({
  className,
  children,
  ...props
}: {
  className?: string;
  children: React.ReactNode;
  props?: React.ComponentProps<'span'>;
}) {
  return (
    <span
      className={cn(
        'gbif-identifierType g-px-2 g-rounded-s g-bg-primary-500 g-border g-border-solid g-border-primary-600 g-border-opacity-20 g-text-primaryContrast-500',
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}

export function IdentifierValue({
  className,
  children,
  ...props
}: {
  className?: string;
  children?: React.ReactNode;
  props?: React.ComponentProps<'span'>;
}) {
  return (
    <span
      className={cn(
        'g-px-2 g-rounded-e g-border-solid g-border g-border-solid g-border-s-0 g-border-slate-800 g-border-opacity-20 ',
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}

export const IdentifierTag = React.forwardRef(
  (
    {
      as: Div = 'div',
      className,
      children,
      ...props
    }: {
      as?: React.ElementType;
      className?: string;
      children?: React.ReactNode;
    } & React.ComponentProps<'a'>,
    ref
  ) => {
    return (
      <Div
        dir="auto"
        ref={ref}
        className={cn(
          'g-inline-block g-text-sm g-text-inherit g-no-underline [&>.gbif-identifierType]:hover:g-primary-600 [&>.gbif-identifierType]:hover:g-text-primaryContrast-600',
          className
        )}
        {...props}
        children={children}
      />
    );
  }
);
